import { ReactElement, useEffect, useState, type FC } from 'react';

import { HelpImage } from '@pp/core/src/components/help/utils/HelpImage';
import { HelpText } from '@pp/core/src/services/helpTranslations/reactBindings/HelpText';
import { useUITranslation } from '@pp/core/src/services/uiTranslations/reactBindings/hooks';
import { useConfigSlice } from '@pp/core/src/store/slices/config/selectors';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { getStaticResourceUrl } from '@pp/core/src/utils/getStaticBaseUrl';

import { HIGHFLYER_PT } from '../../../config';
import { TableBetsConnected } from '../../tableSheet/tableBets/TableBetsConnected';
import { InGameHistoryConnected } from '../../tableSheet/tableHistory/InGameHistoryConnected';
import { TableTopCashedMultipliers } from '../../tableSheet/tableTop/cashedMultipliersAndWins/TableTopCashedMultipliers';
import { TableTopConnected } from '../../tableSheet/tableTop/TableTopConnected';
import { TableTabs } from '../../tableTabs/TableTabs';
import { IMG_TYPES, AVAILABLE_CURRENCIES, IMG_TYPES_PT } from '../types';

import styles from './CrashHelpChapterLeaderBoardAndStatistics.module.css';

const componentMapTop: Record<string, ReactElement> = {
  'CASHED MULTIPLIERS': <TableTopCashedMultipliers showStaticData={true} />,
  '': <></>,
};

const betsMap: Record<string, ReactElement> = {
  Bets: <TableBetsConnected showStaticData={{ static: true, previous: false }} />,
  History: <></>,
  Top: <></>,
  Stats: <></>,
};

const previousMap: Record<string, ReactElement> = {
  Bets: <TableBetsConnected showStaticData={{ static: true, previous: true }} />,
  History: <></>,
  Top: <></>,
  Stats: <></>,
};

const historyMap: Record<string, ReactElement> = {
  Bets: <></>,
  History: <InGameHistoryConnected showStaticData={true} />,
  Top: <></>,
  Stats: <></>,
};
const topMap: Record<string, ReactElement> = {
  Bets: <></>,
  History: <></>,
  Top: <TableTopConnected componentMapTop={componentMapTop} showStaticData={true} />,
  Stats: <></>,
};

export const CrashHelpChapterLeaderBoardAndStatistics: FC = () => {
  const tableName = useConfigSlice.tableName() ?? '';
  const highFlyerPT = useConfigSlice.getTableVariant() === HIGHFLYER_PT;
  const tName = useUITranslation(tableName) ?? tableName;
  const currency = useGameStateSlice.getCurrency();
  const types = highFlyerPT ? IMG_TYPES_PT : IMG_TYPES;
  const [imageUrls, setImageUrls] = useState(new Array(Object.keys(types).length).fill(''));

  useEffect(() => {
    const imgTypes = Object.entries(types);
    imgTypes.forEach(([, value], index) => {
      searchImage(value, index);
    });
  }, []);

  const searchImage = (imgType: string, index: number) => {
    const helpAssetsUrl = getStaticResourceUrl(`assets/helpAssets/${imgType}.png`);
    const otherAssetsUrl = getStaticResourceUrl(`assets/${currency?.toLowerCase()}Assets/${imgType}.png`);
    const newUrl =
      currency && currency.toUpperCase() in AVAILABLE_CURRENCIES && !highFlyerPT ? otherAssetsUrl : helpAssetsUrl;
    setImageUrls((prevUrls) =>
      prevUrls.map((url, i) => {
        if (i === index) return newUrl;
        else return url;
      })
    );
  };

  if (!tableName) return null;

  return (
    <div>
      <p>
        <HelpText tk="leaderboardAndStatistics.leaderboardDescription" />
      </p>
      <p>
        <HelpText tk="leaderboardAndStatistics.leaderboard" />
      </p>
      <p>
        <HelpText tk="leaderboardAndStatistics.betsSection" />
      </p>
      <p>
        <HelpText tk="leaderboardAndStatistics.leaderboardSummary" />
      </p>
      <div className={styles.tableTabsContainer}>
        <TableTabs
          classNameLabel={styles.tableSheetHeader}
          tabs={Object.keys(betsMap).map((tab) => ({
            label: <>{tab}</>,
            content: betsMap[tab],
            key: tab,
          }))}
        />
      </div>
      {/* <HelpImage url={imageUrls[0]} alt="LeaderBoard" /> */}
      <p>
        <HelpText tk="leaderboardAndStatistics.previousRound" markdown />
      </p>
      <div className={styles.tableTabsContainer}>
        <TableTabs
          classNameLabel={styles.tableSheetHeader}
          tabs={Object.keys(previousMap).map((tab) => ({
            label: <>{tab}</>,
            content: previousMap[tab],
            key: tab,
          }))}
        />
      </div>
      {/* <HelpImage url={imageUrls[1]} alt="PreviousRound" /> */}
      <p>
        <HelpText tk="leaderboardAndStatistics.history" />
      </p>
      <p>
        <HelpText dynamicValues={{ TABLE_NAME: tName }} tk="leaderboardAndStatistics.historySection" markdown />
      </p>
      <div className={styles.tableTabsContainer}>
        <TableTabs
          classNameLabel={styles.tableSheetHeader}
          initActiveTab={1}
          tabs={Object.keys(historyMap).map((tab) => ({
            label: <>{tab}</>,
            content: historyMap[tab],
            key: tab,
          }))}
        />
      </div>
      {/* <HelpImage url={imageUrls[2]} alt="History" /> */}
      <p>
        <HelpText tk="leaderboardAndStatistics.top" />
      </p>
      <p>
        <HelpText tk="leaderboardAndStatistics.topSection" />
      </p>
      <div className={styles.tableTabsContainer}>
        <TableTabs
          classNameLabel={styles.tableSheetHeader}
          initActiveTab={2}
          tabs={Object.keys(topMap).map((tab) => ({
            label: <>{tab}</>,
            content: topMap[tab],
            key: tab,
          }))}
        />
      </div>
      {/* <HelpImage url={imageUrls[3]} alt="Top" /> */}
      <p>
        <HelpText tk="leaderboardAndStatistics.stats" />
      </p>
      <p>
        <HelpText tk="leaderboardAndStatistics.statsSection" />
      </p>
      <HelpImage url={imageUrls[4]} alt="Stats" />
    </div>
  );
};
